import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const defaultMeta = {
  title: "Trey Reynolds | AI & Technical Consulting — Polity Interfaces",
  description:
    "Practical AI and technical consulting with Trey Reynolds, CTO at Abilitie. Turn business problems into useful tools and make clearer software decisions.",
  image: "https://polityinterfaces.com/og-image.png",
  imageWidth: "1200",
  imageHeight: "630",
  type: "website",
};

const pageMeta = {
  "/": {
    title: "Trey Reynolds | AI & Technical Consulting — Polity Interfaces",
    description:
      "Practical AI and technical consulting with Trey Reynolds, CTO at Abilitie. Turn business problems into useful tools and make clearer software decisions.",
    type: "website",
  },
  "/about": {
    title: "About Trey Reynolds | Polity Interfaces",
    description:
      "Meet Trey Reynolds: CTO at Abilitie leading practical AI work, co-builder of the Starlink mobile app, and web engineer since 2005.",
    type: "profile",
  },
  "/services": {
    title: "Expertise & Consulting | Trey Reynolds — Polity Interfaces",
    description:
      "Practical AI for business, mobile and web engineering, connected products, and engineering leadership. Focused consulting and projects with Trey Reynolds.",
    type: "website",
  },
  "/projects": {
    title: "The Lab | Signal for Families & Other Projects — Polity Interfaces",
    description:
      "Explore Signal for Families, interactive simulations, and software experiments by Trey Reynolds.",
    type: "website",
  },
  "/contact": {
    title: "Contact Trey Reynolds | Polity Interfaces",
    description:
      "Talk with Trey Reynolds about a workflow to improve, a practical AI project, or a software and engineering leadership decision.",
    type: "website",
  },
  "/blog": {
    title: "Blog | AI Development & Tech Insights",
    description:
      "Insights and articles about AI development, web technologies, and technical consulting from industry experts.",
    type: "blog",
  },
  "/privacy": {
    title: "Privacy Policy | Polity Interfaces",
    description:
      "Our commitment to protecting your privacy and data security at Polity Interfaces.",
    type: "website",
  },
  "/projects/cfb-rankings": {
    title: "CFB Rankings | Polity Interfaces",
    description: "CFB Rankings powered by Polity Interfaces.",
    type: "website",
  },
};

export default function SEO() {
  const location = useLocation();
  const currentPath = location.pathname.endsWith("/")
    ? location.pathname.slice(0, -1)
    : location.pathname;

  useEffect(() => {
    const meta = { ...defaultMeta, ...(pageMeta[currentPath] || {}) };

    // Basic meta tags
    document.title = meta.title;
    document.querySelector('meta[name="description"]')?.setAttribute("content", meta.description);

    // Open Graph tags
    document.querySelector('meta[property="og:title"]')?.setAttribute("content", meta.title);
    document
      .querySelector('meta[property="og:description"]')
      ?.setAttribute("content", meta.description);
    document.querySelector('meta[property="og:image"]')?.setAttribute("content", meta.image);
    document
      .querySelector('meta[property="og:image:width"]')
      ?.setAttribute("content", meta.imageWidth);
    document
      .querySelector('meta[property="og:image:height"]')
      ?.setAttribute("content", meta.imageHeight);
    document.querySelector('meta[property="og:type"]')?.setAttribute("content", meta.type);
    document
      .querySelector('meta[property="og:url"]')
      ?.setAttribute("content", `https://polityinterfaces.com${currentPath}`);

    // Twitter tags
    document.querySelector('meta[property="twitter:title"]')?.setAttribute("content", meta.title);
    document
      .querySelector('meta[property="twitter:description"]')
      ?.setAttribute("content", meta.description);
    document.querySelector('meta[property="twitter:image"]')?.setAttribute("content", meta.image);
    document
      .querySelector('meta[name="twitter:card"]')
      ?.setAttribute("content", "summary_large_image");

    // Canonical URL
    const canonicalUrl = `https://polityinterfaces.com${currentPath}`;
    let canonicalTag = document.querySelector('link[rel="canonical"]');
    if (!canonicalTag) {
      canonicalTag = document.createElement("link");
      canonicalTag.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalTag);
    }
    canonicalTag.setAttribute("href", canonicalUrl);

    // Schema.org JSON-LD
    let scriptTag = document.querySelector("#schema-org");
    if (!scriptTag) {
      scriptTag = document.createElement("script");
      scriptTag.id = "schema-org";
      scriptTag.type = "application/ld+json";
      document.head.appendChild(scriptTag);
    }

    const schemaData = {
      "@context": "https://schema.org",
      "@type": meta.type === "profile" ? "Person" : "WebPage",
      "@id": canonicalUrl,
      url: canonicalUrl,
      name: meta.title,
      description: meta.description,
      image: meta.image,
      ...(meta.type === "profile" && {
        givenName: "Trey",
        familyName: "Reynolds",

        jobTitle: "Chief Technology Officer",
        worksFor: {
          "@type": "Organization",
          name: "Abilitie",
        },
      }),
    };

    scriptTag.textContent = JSON.stringify(schemaData);
  }, [currentPath]);

  return null;
}
